'use client';

import { ArrowUpLeft, ArrowUpRight } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import { Button } from '@/components/ui/button';
import { LinkPreview } from '@/components/ui/link-preview';
import { useTranslation } from '@/lib/i18n/context';

const WorkflowCapabilities = () => {
  const { t } = useTranslation();

  const features = [
    {
      id: 1,
      title: t.features_pages['Drag-and-Drop Builder'],
      description:
        t.features_pages[
          'Visual workflow builder. No code required. Map complex multi-party communication flows with a simple interface.'
        ],
    },
    {
      id: 2,
      title: t.features_pages['15 Triggers Available'],
      description:
        t.features_pages[
          'Form submissions, webhooks, CRM updates, time-based triggers, and more. Start workflows from any event.'
        ],
    },
    {
      id: 3,
      title: t.features_pages['25 Actions to Execute'],
      description:
        t.features_pages[
          'AI phone calls, outbound emails, WhatsApp messages, CRM updates, conditional logic, delays, and more.'
        ],
    },
    {
      id: 4,
      title: t.features_pages['Multi-Party Orchestration'],
      description:
        t.features_pages[
          'Coordinate communication between multiple parties. Make calls, wait for responses, follow up automatically—mimicking human behavior at scale.'
        ],
    },
    {
      id: 5,
      title: t.features_pages['Conditional Logic & Branching'],
      description:
        t.features_pages[
          'If/then logic based on responses. Branch workflows depending on what people say or do. Handle every scenario.'
        ],
    },
  ];

  return (
    <section className="bg-[rgb(11,21,25)] py-32 text-white">
      <div className="container mx-auto mb-32">
        <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center">
          <h2 className="font-serif text-2xl text-white md:text-4xl">
            {t.features_pages["Automate what can't be automated"]}
          </h2>
          <p className="text-sm text-white/80 md:text-base">
            {
              t.features_pages[
                "Complex multi-party flows that traditional automation can't handle. Agentic workflows map human behavior patterns and execute them at scale."
              ]
            }
          </p>
        </div>
        <div className="mb-32 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20 md:p-7"
            >
              <h3 className="font-serif text-lg text-white md:text-xl">
                {feature.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function WorkflowsPage() {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {t.features_pages['Agentic Workflows']}
            <br />
            {t.features_pages['Automate the unautomatable']}
          </>
        }
        subtitle={
          t.features_pages[
            "Map and automate complex communication flows that traditional automation can't handle. Multi-party coordination, conditional logic, human-like behavior—executed at scale."
          ]
        }
      />

      <Logos />

      <BenefitsSection
        title={t.features_pages['Workflow Capabilities']}
        subtitle={
          t.features_pages['Automate complex multi-party communication']
        }
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="black"
        benefits={[
          {
            title: t.features_pages['Automate Manual Communication'],
            description:
              t.features_pages[
                'Map and automate complex communication flows between parties without APIs. Just manual communication at scale.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/workflows/automate-manual-communication.mp4',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['Phone → Email → Follow-up Chains'],
            description:
              t.features_pages[
                'Call multiple parties. When one responds positively, automatically send emails, schedule follow-ups, and coordinate next steps.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/workflows/phone-email-follow-up-chains.mp4',
            imagePosition: 'left',
          },
          {
            title: t.features_pages['Human Behavior at Scale'],
            description:
              t.features_pages[
                'One-to-one mapping of human communication patterns. Agentic workflows mimic how humans would handle complex scenarios.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/workflows/human-behavior-at-scale.mp4',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['15 Triggers, 25 Actions'],
            description:
              t.features_pages[
                'Comprehensive trigger and action library. AI phone calling, outbound sequences, emails, WhatsApp, form submissions, CRM integration.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/workflows/triggers-actions.mp4',
            imagePosition: 'left',
          },
        ]}
      />

      {/* Authentic Message Section */}
      <section className="overflow-hidden py-32">
        <div className="container w-full">
          <h2 className="text-muted-foreground/40 mt-10 text-center font-serif text-3xl md:text-4xl">
            {isAr ? (
              <>
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Workflow"
                  className="px-1"
                >
                  سير العمل
                </LinkPreview>{' '}
                التقليدي يحتاج APIs وبيانات منظّمة. ويتعطّل أول ما يصير التواصل
                يدوياً.
                <br />
                <br />
                سير العمل الذكي يفهم{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Natural_language_processing"
                  className="px-1"
                >
                  اللغة الطبيعية
                </LinkPreview>
                ، ينتظر ردود البشر، ويتأقلم مع المواقف. ينسّق التواصل بين عدة
                أطراف كما يفعل الإنسان — بس على نطاق واسع.
                <br />
                <br />
                مو مجرد أتمتة. تنسيق أنماط تواصل معقّدة تشبه تواصل البشر.
              </>
            ) : (
              <>
                Traditional{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Workflow"
                  className="px-1"
                >
                  workflows
                </LinkPreview>{' '}
                require APIs and structured data. They break when communication
                is manual.
                <br />
                <br />
                Agentic workflows understand{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Natural_language_processing"
                  className="px-1"
                >
                  natural language
                </LinkPreview>
                , wait for human responses, adapt to situations. They coordinate
                multi-party communication like a human would—but at scale.
                <br />
                <br />
                Not just automation. Orchestration of complex human-like
                communication patterns.
              </>
            )}
          </h2>
        </div>
      </section>

      {/* Capabilities Section - Dark */}
      <WorkflowCapabilities />

      {/* Triggers and Actions Section */}
      <section className="bg-white py-32">
        <div className="container">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="mb-4 font-serif text-2xl md:text-4xl">
              {t.features_pages['15 Triggers, 25 Actions']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.features_pages[
                  'Comprehensive library of triggers and actions to build any workflow you can imagine.'
                ]
              }
            </p>
          </div>
          <div className="mx-auto max-w-4xl space-y-12">
            <div>
              <h3 className="mb-6 text-xl">
                {t.features_pages['Available Triggers (15)']}
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['Form Submissions']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['WhatsApp Webhooks']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">{t.features_pages['CRM Updates']}</p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['Time-based Triggers']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['Email Received']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['+10 more triggers']}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="mb-6 text-xl">
                {t.features_pages['Available Actions (25)']}
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['AI Phone Calling']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['Outbound Email Sequences']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['WhatsApp Messages']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['CRM Integration']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['Conditional Logic']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['Wait for Response']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['AI Decision Making']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['Data Transformation']}
                  </p>
                </div>
                <div className="border-border rounded-lg border p-4">
                  <p className="text-sm">
                    {t.features_pages['+17 more actions']}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-center pt-8">
              <Button size="lg">
                {t.features_pages['View All Triggers & Actions']}{' '}
                {isAr ? (
                  <ArrowUpLeft className="ms-2 size-4" />
                ) : (
                  <ArrowUpRight className="ms-2 size-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.features_pages[
            'Find answers to common questions about Agentic Workflows.'
          ]
        }
        items={[
          {
            question:
              t.features_pages['What makes Agentic Workflows different?'],
            answer:
              t.features_pages[
                'Traditional automation requires APIs and structured data. Agentic workflows handle manual communication between parties—they understand responses, wait for replies, and adapt like humans do.'
              ],
          },
          {
            question: t.features_pages['Do I need to code to build workflows?'],
            answer:
              t.features_pages[
                'No. Drag-and-drop visual builder. Connect triggers to actions, set conditions, and map your flow visually.'
              ],
          },
          {
            question: t.features_pages['Can workflows make phone calls?'],
            answer:
              t.features_pages[
                'Yes. AI phone calling is one of 25 available actions. Make calls, wait for responses, and follow up based on what was said.'
              ],
          },
          {
            question:
              t.features_pages['How do workflows handle multiple parties?'],
            answer:
              t.features_pages[
                'Workflows can coordinate communication between unlimited parties. Call person A, email person B, wait for C to respond, then trigger next steps based on all inputs.'
              ],
          },
          {
            question: t.features_pages['What triggers are available?'],
            answer:
              t.features_pages[
                'Form submissions, WhatsApp webhooks, CRM updates, time-based triggers, email received, chat messages, and 9 more trigger types.'
              ],
          },
          {
            question: t.features_pages['Can workflows integrate with my CRM?'],
            answer:
              t.features_pages[
                'Yes. Third-party CRM integration is supported as both a trigger and an action. Sync data, update records, and coordinate with your existing systems.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
